/**
 * The analytics answer this device remembers (rl-lif9, DPIA P6).
 *
 * Two kinds of record, both strictly necessary preference records rather
 * than analytics storage:
 *
 * - `revisionlab.analytics_choice` — the answer of a visitor without an
 *   account. For them this record *is* the consent: "accepted" here is what
 *   lets Statsig start, and nothing else does.
 * - `revisionlab.analytics_choice.<userId>` — a signed-in user's refusal,
 *   kept only so the banner does not re-ask on every visit. An account's
 *   consent lives on the server (`consents`), so "accepted" is never stored
 *   for one. Nothing is copied between the two kinds: a choice made on a
 *   shared device without an account cannot record consent for whoever
 *   signs in next.
 *
 * Each record carries the `POLICY_VERSION` it answered. A record for any
 * other version reads as no answer, so new wording asks again.
 */
import { POLICY_VERSION } from "./policy";

export type AnalyticsChoice = "accepted" | "rejected";

const ANONYMOUS_KEY = "revisionlab.analytics_choice";

function storageKey(userId: string | null): string {
  return userId === null ? ANONYMOUS_KEY : `${ANONYMOUS_KEY}.${userId}`;
}

/** The answer remembered for `userId` (null: no account) at this policy version. */
export function readAnalyticsChoice(
  userId: string | null,
): AnalyticsChoice | null {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const record = JSON.parse(raw) as {
      choice?: unknown;
      policyVersion?: unknown;
    };
    if (record.policyVersion !== POLICY_VERSION) return null;
    return record.choice === "accepted" || record.choice === "rejected"
      ? record.choice
      : null;
  } catch {
    // Unreadable or storage disabled: no answer, so the banner asks.
    return null;
  }
}

export function recordAnalyticsChoice(
  userId: string | null,
  choice: AnalyticsChoice,
): void {
  try {
    localStorage.setItem(
      storageKey(userId),
      JSON.stringify({ choice, policyVersion: POLICY_VERSION }),
    );
  } catch {
    // Storage disabled: the answer holds for this page only.
  }
}

export function forgetAnalyticsChoice(userId: string): void {
  try {
    localStorage.removeItem(storageKey(userId));
  } catch {
    // Storage disabled: there is nothing to forget.
  }
}
